import { useState } from 'react';
import { components } from 'react-select';
import api from '@shared/api';
import { useFlags } from 'launchdarkly-react-client-sdk';
import COLORS from '@shared/colorConstants';
import routes from '@root/routes';
import { BuildingAnalyticsInformation, GlobalSearchResponse } from '@root/types';
import CustomIcon, { ValidIconTypes } from '@components/shared/CustomIcon';
import { useTranslation } from 'react-i18next';
import AsyncSelect from 'react-select/async';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import actions from '@store/actions/homepage/analyticsActions';
import s from './HomepageSearch.module.less';

const disableBlueBorder = {
  border: 0,
  boxShadow: 'none',
};

const customStyles = {
  container: base => ({
    ...base,
    height: '100%',
  }),
  control: base => ({
    ...base,
    height: '100%',
    ...disableBlueBorder,
  }),
  placeholder: base => ({
    ...base,
    fontSize: 'inherit',
    color: '#000000',
  }),
  input: base => ({
    ...base,
    fontSize: 'inherit',
    color: '#000000',
  }),
  groupHeading: base => ({
    ...base,
    marginBottom: 0,
    paddingLeft: 0,
    paddingRight: 0,
  }),
  group: base => ({
    ...base,
    paddingTop: 0,
    paddingBottom: 0,
  }),
  menuList: base => ({
    ...base,
    paddingTop: 0,
    paddingBottom: 0,
  }),
  option: (base, state) => ({
    ...base,
    background: state.isFocused ? COLORS.egg50 : 'white',
  }),
};

type OptionType = {
  label: string;
  value: string;
  group: 'building' | 'location';
  labelAsideText: string;
  meta: { market: string } | { analytics: BuildingAnalyticsInformation };
};

type GroupType = {
  label: string;
  icon: ValidIconTypes;
  options: OptionType[];
};

const Control = ({ children, ...props }: any) => (
  <components.Control {...props}>
    <CustomIcon type="magnifying-glass" className={s.searchIcon} />
    {children}
  </components.Control>
);

const formatGroupLabel = ({ label, icon }: GroupType) => (
  <div className={s.groupLabel}>
    <CustomIcon type={icon} className={s.groupLabelIcon} />
    {label}
  </div>
);

const Option = props => {
  /* eslint-disable react/no-danger */
  /* eslint-disable react/destructuring-assignment */
  return (
    <components.Option {...props}>
      <div dangerouslySetInnerHTML={{ __html: props.data.label }} />
      <div>{props.data.labelAsideText}</div>
    </components.Option>
  );
};
/* eslint-enable react/no-danger */
/* eslint-enable react/destructuring-assignment */

const customComponents = {
  DropdownIndicator: null,
  IndicatorSeparator: null,
  Control,
  Option,
};

const HomePageSearch = () => {
  const { t } = useTranslation('homepage');
  const [searchValue, setSeachValue] = useState();
  const history = useHistory();
  const dispatch = useDispatch();
  const flags = useFlags();

  const handleChange = ({ value: slug, group, meta }: OptionType, { action }) => {
    if (action === 'select-option') {
      if (group === 'building') {
        dispatch(actions.searchBuildingSelected());
        history.push(routes.building(slug));
      } else if (group === 'location') {
        dispatch(actions.searchLocationSelected({ filterLocation: slug }));
        const url = flags['new-search-redirect']
          ? `${routes.search((meta as { market: string }).market)}?submarkets=${slug}`
          : routes.officeSpaceWithOneMarket(slug);
        history.push(url);
      }
    }
  };

  const handleInputChanged = (value, { action }) => {
    if (action === 'input-change') {
      setSeachValue(value);
    }
  };

  const loadOptions = async (inputValue): Promise<GroupType[]> => {
    const response = await api.fetch(routes.api.globalSearches(inputValue));
    const rawSearchResults: GlobalSearchResponse = await response.json();

    return [
      {
        label: t('search.locationsGroupLabel'),
        icon: 'pin-drop',
        options: rawSearchResults.locations.map(
          (l): OptionType => ({
            label: l.highlightedLabel,
            value: l.value,
            group: 'location',
            labelAsideText: l.labelAsideText,
            meta: l.meta as { market: string },
          }),
        ),
      },

      {
        label: t('search.buildingsGroupLabel'),
        icon: 'buildings-multiple',
        options: rawSearchResults.buildings.map(
          (b): OptionType => ({
            label: b.highlightedLabel,
            value: b.value,
            group: 'building',
            labelAsideText: b.labelAsideText,
            meta: b.meta,
          }),
        ),
      },
    ];
  };

  return (
    <div className={s.container}>
      {/* @ts-expect-error */}
      <AsyncSelect
        className={s.search}
        name="home-page-search"
        loadOptions={loadOptions}
        styles={customStyles}
        components={customComponents}
        onChange={handleChange}
        placeholder={t('search.placeholder')}
        formatGroupLabel={formatGroupLabel}
        aria-label={t('search.inputLabel')}
        onInputChange={handleInputChanged}
        inputValue={searchValue}
        openMenuOnClick={false}
        noOptionsMessage={() => t('search.noResultsMessage')}
      />
    </div>
  );
};

export default HomePageSearch;
